/**
 * EditTicketCatalogModal - 기존 수강권 카탈로그를 수정하는 모달 (instructor-web)
 *
 * admin-web의 동명 모달을 instructor-web 컨벤션(design-system)으로 이식한 것.
 * 강사 전용 `updateInstructorTicketCatalog`를 호출하므로 강사도 수정 가능.
 * 초기값 prefill + 변경 필드만 전송. validDays/maxTrainee를 비우면 null(제거)로 전송.
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { Card, Button, Alert } from "@vlab-corporation/design-system";
import { useUpdateTicketCatalog, type UpdateTicketCatalogParams } from "@/hooks/useTicketCatalog";
import type { TicketCatalogItem } from "@/services/firestore/ticketCatalog";

interface EditTicketCatalogModalProps {
  isOpen: boolean;
  centerId: number;
  ticket: TicketCatalogItem;
  onClose: () => void;
  onUpdated: () => void;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  return null;
}

/** 양의 정수 문자열만 파싱. 빈 칸/비정수/0 이하는 null. */
function parsePositiveInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n > 0 ? n : null;
}

export function EditTicketCatalogModal({
  isOpen,
  centerId,
  ticket,
  onClose,
  onUpdated,
}: EditTicketCatalogModalProps) {
  const [title, setTitle] = useState(ticket.title);
  const [type, setType] = useState<"P" | "G">(ticket.type);
  const [totalCouponInput, setTotalCouponInput] = useState(String(ticket.totalCoupon));
  const [validDaysInput, setValidDaysInput] = useState(
    ticket.validDays != null ? String(ticket.validDays) : "",
  );
  const [maxTraineeInput, setMaxTraineeInput] = useState(
    ticket.maxTrainee != null ? String(ticket.maxTrainee) : "",
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateTicketCatalog = useUpdateTicketCatalog();

  useEffect(() => {
    if (isOpen) {
      setTitle(ticket.title);
      setType(ticket.type);
      setTotalCouponInput(String(ticket.totalCoupon));
      setValidDaysInput(ticket.validDays != null ? String(ticket.validDays) : "");
      setMaxTraineeInput(ticket.maxTrainee != null ? String(ticket.maxTrainee) : "");
      setValidationError(null);
      updateTicketCatalog.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticket]);

  if (!isOpen) return null;

  /**
   * 선택 정수 필드의 변경분 계산.
   * - 빈 칸 + 기존값 있음 → null(제거), 빈 칸 + 기존값 없음 → undefined(변경 없음)
   * - 값 입력 + 유효 + 기존과 다름 → number, 같으면 undefined
   * - 잘못된 값 → throw(검증 에러)
   */
  function diffOptionalInt(
    input: string,
    initial: number | undefined,
    label: string,
  ): number | null | undefined {
    const trimmed = input.trim();
    if (trimmed === "") {
      return initial != null ? null : undefined;
    }
    const parsed = parsePositiveInt(trimmed);
    if (parsed === null) {
      throw new Error(`${label}은(는) 1 이상의 정수로 입력해주세요 (또는 빈 칸).`);
    }
    return parsed === initial ? undefined : parsed;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setValidationError("수강권명을 입력해주세요.");
      return;
    }
    const totalCoupon = parsePositiveInt(totalCouponInput);
    if (totalCoupon === null) {
      setValidationError("총 횟수는 1 이상의 정수로 입력해주세요.");
      return;
    }

    let validDays: number | null | undefined;
    let maxTrainee: number | null | undefined;
    try {
      validDays = diffOptionalInt(validDaysInput, ticket.validDays, "사용기한");
      maxTrainee = diffOptionalInt(maxTraineeInput, ticket.maxTrainee, "수업당 최대 인원");
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : "입력값을 확인해주세요.");
      return;
    }

    const params: UpdateTicketCatalogParams = { centerId, ticketId: ticket.id };
    if (trimmedTitle !== ticket.title) params.title = trimmedTitle;
    if (type !== ticket.type) params.type = type;
    if (totalCoupon !== ticket.totalCoupon) params.totalCoupon = totalCoupon;
    if (validDays !== undefined) params.validDays = validDays;
    if (maxTrainee !== undefined) params.maxTrainee = maxTrainee;

    const changedKeys = Object.keys(params).filter((k) => k !== "centerId" && k !== "ticketId");
    if (changedKeys.length === 0) {
      setValidationError("변경된 내용이 없습니다.");
      return;
    }

    setValidationError(null);
    updateTicketCatalog.mutate(params, { onSuccess: () => onUpdated() });
  };

  const isSubmitDisabled =
    !title.trim() || !totalCouponInput.trim() || updateTicketCatalog.isPending;
  const serverErrorMessage = updateTicketCatalog.isError
    ? getErrorMessage(updateTicketCatalog.error)
    : null;

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <Card className="w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">수강권 수정</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="editTicketTitle"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              수강권명 <span className="text-red-500">*</span>
            </label>
            <input
              id="editTicketTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editTicketType"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Type <span className="text-red-500">*</span>
            </label>
            <select
              id="editTicketType"
              value={type}
              onChange={(e) => setType(e.target.value as "P" | "G")}
              className={`${inputClass} bg-white`}
            >
              <option value="P">P (Private)</option>
              <option value="G">G (Group)</option>
            </select>
          </div>

          <div className="mb-4">
            <label
              htmlFor="editTicketTotalCoupon"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              총 횟수 <span className="text-red-500">*</span>
            </label>
            <input
              id="editTicketTotalCoupon"
              type="number"
              step={1}
              value={totalCouponInput}
              onChange={(e) => setTotalCouponInput(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editTicketValidDays"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              사용기한 (일) <span className="text-xs text-gray-400">— 비우면 무제한</span>
            </label>
            <input
              id="editTicketValidDays"
              type="number"
              step={1}
              value={validDaysInput}
              onChange={(e) => setValidDaysInput(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editTicketMaxTrainee"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              수업당 최대 인원 <span className="text-xs text-gray-400">— 비우면 미지정</span>
            </label>
            <input
              id="editTicketMaxTrainee"
              type="number"
              step={1}
              value={maxTraineeInput}
              onChange={(e) => setMaxTraineeInput(e.target.value)}
              className={inputClass}
            />
          </div>

          {validationError && (
            <Alert variant="error" className="mb-4">
              {validationError}
            </Alert>
          )}

          {updateTicketCatalog.isError && !validationError && (
            <Alert variant="error" className="mb-4">
              {serverErrorMessage ?? "수강권 수정에 실패했습니다. 다시 시도해주세요."}
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitDisabled}>
              {updateTicketCatalog.isPending ? "저장 중..." : "저장"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
